import over from 'lodash/over';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import {
  Button,
  ButtonVariant,
  Icon,
  IconName,
} from '@usealloy/component-library';

import CustomizableDataDropdown from '~/components/CustomizableDataDropdown';

export const DEFAULT_PAGE_LENS = [20, 50, 100];

/* TablePaginateFilter
 * Allows user to change the amount that the table data is paginated by
 */
export const TablePaginateFilterPropTypes = {
  dropdownProps: PropTypes.object,
  paginateBy: PropTypes.number,
  updatePaginateBy: PropTypes.func,
  customPageLengths: PropTypes.arrayOf(PropTypes.number),
};

const TablePaginateFilterDefaultProps = {
  dropdownProps: {},
  customPageLengths: [],
};

function TablePaginateFilter({
  customPageLengths, dropdownProps, paginateBy, updatePaginateBy,
}) {
  const getItemClasses = useCallback(
    item => (item === paginateBy ? 'selected' : ''),
    [paginateBy],
  );

  const isValidPageLenProp = customPageLengths.length > 0
    && customPageLengths.every(num => Number.isInteger(num));

  return (
    <CustomizableDataDropdown
      isArrayScalar
      availableOptions={(
        isValidPageLenProp ? customPageLengths : DEFAULT_PAGE_LENS
      )}
      dropdownProps={dropdownProps}
      dropdownTag="button"
      isDropdownWithInput={false}
      itemsFilterCallback={over}
      itemClassNameCallback={getItemClasses}
      onClick={updatePaginateBy}
      toggleClass="btn btn-secondary secondary nocase"
      toggleDisplay={`${paginateBy} per page`}
    />
  );
}

TablePaginateFilter.propTypes = TablePaginateFilterPropTypes;
TablePaginateFilter.defaultProps = TablePaginateFilterDefaultProps;

/* TablePagination
 * Displays series of links for table pages, handles events
 * relating to selecting a specific page, going to next or previous page
 */
const TablePaginationPropTypes = {
  className: PropTypes.string,
  indexByZero: PropTypes.bool,
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  updatePage: PropTypes.func.isRequired,
};

const TablePaginationDefaultProps = {
  className: '',
  indexByZero: true,
};

const TablePagination = ({
  className,
  indexByZero,
  page,
  pages,
  updatePage,
}) => {
  const currentPage = indexByZero ? page : page - 1;

  const pageLinks = (() => {
    // Defaults
    let pageStart = 0;
    let pageEnd = pages;
    let endBuffer = false;

    let links = [];
    const startBuffer = { link: false, index: 0 };

    if (pages > 9) {
      if (currentPage < 4) {
        pageEnd = 9;
        endBuffer = true;
      } else if (currentPage === 4) {
        links = [{ link: true, page: 0, disabled: false }, startBuffer];
        pageStart = 2;
        pageEnd = 9;
        endBuffer = true;
      } else if (currentPage > 4 && currentPage <= pages - 4) {
        links = [{ link: true, page: 0, disabled: false }, startBuffer];
        pageStart = currentPage - 3;
        pageEnd = currentPage + 4;
        endBuffer = true;
      } else {
        links = [{ link: true, page: 0, disabled: false }, startBuffer];
        pageStart = pages - 9;
      }
    }

    for (let p = pageStart; p < pageEnd; p += 1) {
      links.push({ link: true, page: p, disabled: p === currentPage });
    }

    if (endBuffer) links.push({ link: false, index: 1 });

    return links;
  })();


  const handlePageSelection = event => {
    const nextPage = event.target.dataset.page;
    const pageReference = indexByZero ? currentPage : currentPage + 1;

    if (nextPage === 'prev') {
      if (currentPage > 0) updatePage(pageReference - 1);
    } else if (nextPage === 'next') {
      if (currentPage < pages - 1) updatePage(pageReference + 1);
    } else if (+nextPage !== currentPage) {
      const next = indexByZero ? +nextPage : +nextPage + 1;
      updatePage(next);
    }
  };

  return pages > 1 ? (
    <div
      className={`table-pagination flex ${className}`}
      data-testid="table-pagination"
    >
      <Button
        data-page="prev"
        onClick={handlePageSelection}
        style={{ color: 'white' }}
        variant={(
          currentPage === 0
            ? ButtonVariant.secondary
            : ButtonVariant.primary
        )}
      >
        <Icon data-page="prev" icon={IconName.CaretLeft} color="white" />
      </Button>

      {pageLinks.map(link => (link.link ? (
        <a
          key={link.page}
          className={link.disabled ? 'selected' : ''}
          data-page={link.page}
          onClick={link.disabled ? () => {} : handlePageSelection}
        >
          {link.page + 1}
        </a>
      ) : (
        <a
          key={`...${link.index}`}
          className="disabled"
        >
          ...
        </a>
      )))}

      <Button
        data-page="next"
        onClick={handlePageSelection}
        style={{ color: 'white' }}
        variant={(
          currentPage === pages - 1
            ? ButtonVariant.secondary
            : ButtonVariant.primary
        )}
      >
        <Icon data-page="next" icon={IconName.CaretRight} color="white" />
      </Button>
    </div>
  ) : null;
};

TablePagination.propTypes = TablePaginationPropTypes;
TablePagination.defaultProps = TablePaginationDefaultProps;

export { TablePaginateFilter, TablePagination };
